/**
 * pendingAuthIntent — versioned, TTL-bounded, idempotent persistence
 * for the AuthActivity round-trip.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decision D5.
 *
 * Why sessionStorage and not localStorage:
 *   localStorage persists across app restarts → a 3-day-old pending intent
 *   could trigger an unintended payment when the user returns. sessionStorage
 *   scopes to the webview lifecycle, which is the right boundary.
 *   For Capacitor in-app browser OAuth, sessionStorage survives the redirect
 *   chain because the webview is the same.
 *
 * Why a versioned key:
 *   If the schema changes, bump the suffix (v2, v3) so old payloads are
 *   ignored rather than mis-parsed.
 *
 * Why an idempotent consume:
 *   React Strict Mode double-mount + RR navigate-on-mount + OAuth callback
 *   can all cause consume to be called twice. The second call must be a no-op.
 */

import type { PendingAuthIntent } from "./types";

const KEY = "template.pendingAuth.v1";
const TTL_MS = 30 * 60 * 1000; // 30 minutes

interface StoredIntent {
  intent: PendingAuthIntent;
  createdAt: number;
  consumed: boolean;
}

/** Indirection so tests can inject a fake clock. */
type Clock = () => number;
const defaultClock: Clock = () => Date.now();

function safeGetStorage(): Storage | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    // Some embedded webviews throw on access (e.g. cookies disabled).
    return null;
  }
}

function readRaw(storage: Storage): StoredIntent | null {
  const raw = storage.getItem(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredIntent;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.createdAt !== "number" ||
      typeof parsed.consumed !== "boolean" ||
      typeof parsed.intent !== "object" ||
      parsed.intent === null
    ) {
      storage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(KEY);
    return null;
  }
}

function isExpired(stored: StoredIntent, now: number): boolean {
  return now - stored.createdAt > TTL_MS;
}

/**
 * Save a new pending intent. Replaces any existing intent (including
 * unconsumed ones — caller is responsible for first checking).
 *
 * Passing { type: "none" } clears the slot.
 */
export function setPendingAuthIntent(
  intent: PendingAuthIntent,
  clock: Clock = defaultClock,
): void {
  const storage = safeGetStorage();
  if (!storage) return;

  if (intent.type === "none") {
    storage.removeItem(KEY);
    return;
  }

  const stored: StoredIntent = {
    intent,
    createdAt: clock(),
    consumed: false,
  };
  try {
    storage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // Quota exceeded or serialization failed — caller has no recovery path.
  }
}

/**
 * Read the pending intent without consuming it.
 * Returns null if absent, expired, consumed, or malformed.
 */
export function peekPendingAuthIntent(
  clock: Clock = defaultClock,
): PendingAuthIntent | null {
  const storage = safeGetStorage();
  if (!storage) return null;

  const stored = readRaw(storage);
  if (!stored) return null;
  if (stored.consumed) return null;
  if (isExpired(stored, clock())) {
    storage.removeItem(KEY);
    return null;
  }
  return stored.intent;
}

/**
 * Consume the pending intent. Marks it consumed atomically before
 * returning, so a second consume() call is a no-op and returns null.
 *
 * Returns null if absent, expired, already consumed, or malformed.
 */
export function consumePendingAuthIntent(
  clock: Clock = defaultClock,
): PendingAuthIntent | null {
  const storage = safeGetStorage();
  if (!storage) return null;

  const stored = readRaw(storage);
  if (!stored) return null;
  if (stored.consumed) return null;
  if (isExpired(stored, clock())) {
    storage.removeItem(KEY);
    return null;
  }

  const consumedRecord: StoredIntent = { ...stored, consumed: true };
  try {
    storage.setItem(KEY, JSON.stringify(consumedRecord));
  } catch {
    // If we can't write the consumed flag, fall through and return the
    // intent anyway — failing to write is rare and worse than running
    // the intent. A duplicate consume in this edge case is acceptable.
  }
  return stored.intent;
}

/**
 * Drop the pending intent without consuming it.
 * Used when the user explicitly cancels the auth flow.
 */
export function discardPendingAuthIntent(): void {
  const storage = safeGetStorage();
  if (!storage) return;
  storage.removeItem(KEY);
}

/** Test-only: exposed for direct sessionStorage assertion. */
export const __PENDING_AUTH_KEY__ = KEY;
export const __PENDING_AUTH_TTL_MS__ = TTL_MS;

/**
 * pendingKujiAction — 쿠지/가챠 상세에서 비로그인 상태로 "참여하기" 를 눌렀다가
 * 로그인 라운드트립을 거친 뒤, 로그인 성공 시 그 액션을 자동 재개하기 위한 마커.
 *
 * pendingAuthIntent(navigate) 로 quantity 페이지를 직접 여는 것은 큐(reserve) 없이
 * 진입해 "잘못된 쿠지" 에러를 유발했다. 대신 auth intent 는 `{ type: "none" }` 으로
 * 두고, 이 마커를 남겨 KujiDetailPage 가 (재)마운트 + 인증 완료 시점에 handleJoinPressed
 * 를 다시 호출 → 쿠지는 정상 reserve→quantity, 가챠는 참여 시트 오픈으로 재개한다.
 *
 * sessionStorage 인 이유는 pendingAuthIntent 와 동일 (webview lifecycle 스코프 +
 * OAuth redirect 체인 생존). kujiSerialNo 로 스코프해 다른 쿠지로 누수 방지.
 */
const KUJI_ACTION_KEY = "template.pendingKujiAction.v1";

/** 어떤 액션을 재개할지 — handleJoinPressed 가 detail 로 재분기하므로 정보성. */
export type KujiResumeAction = "join" | "gacha";

interface StoredKujiAction {
  kujiSerialNo: number;
  action: KujiResumeAction;
  createdAt: number;
}

export function setPendingKujiAction(
  kujiSerialNo: number,
  action: KujiResumeAction,
  clock: Clock = defaultClock,
): void {
  const storage = safeGetStorage();
  if (!storage || !Number.isFinite(kujiSerialNo)) return;
  const stored: StoredKujiAction = { kujiSerialNo, action, createdAt: clock() };
  try {
    storage.setItem(KUJI_ACTION_KEY, JSON.stringify(stored));
  } catch {
    // Quota exceeded — no recovery path.
  }
}

/**
 * 같은 kujiSerialNo 의 마커가 있으면 소비(제거)하고 action 반환. 없거나, 다른 쿠지,
 * 만료, malformed 면 null. 소비는 1회성 — 두 번째 호출은 null.
 */
export function consumePendingKujiAction(
  kujiSerialNo: number,
  clock: Clock = defaultClock,
): KujiResumeAction | null {
  const storage = safeGetStorage();
  if (!storage) return null;
  const raw = storage.getItem(KUJI_ACTION_KEY);
  if (!raw) return null;
  let parsed: StoredKujiAction;
  try {
    parsed = JSON.parse(raw) as StoredKujiAction;
  } catch {
    storage.removeItem(KUJI_ACTION_KEY);
    return null;
  }
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    typeof parsed.kujiSerialNo !== "number" ||
    typeof parsed.createdAt !== "number" ||
    (parsed.action !== "join" && parsed.action !== "gacha")
  ) {
    storage.removeItem(KUJI_ACTION_KEY);
    return null;
  }
  if (clock() - parsed.createdAt > TTL_MS) {
    storage.removeItem(KUJI_ACTION_KEY);
    return null;
  }
  // 다른 쿠지의 마커는 남겨둔다 (그 쿠지 상세 인스턴스가 소비).
  if (parsed.kujiSerialNo !== kujiSerialNo) return null;
  storage.removeItem(KUJI_ACTION_KEY);
  return parsed.action;
}

/** 사용자가 인증을 명시적으로 취소했을 때 등 — 마커 폐기. */
export function discardPendingKujiAction(): void {
  const storage = safeGetStorage();
  if (!storage) return;
  storage.removeItem(KUJI_ACTION_KEY);
}

/** Test-only. */
export const __PENDING_KUJI_ACTION_KEY__ = KUJI_ACTION_KEY;

/**
 * Where the user was when auth opened — preserved across OAuth roundtrip so we
 * can return them to that exact screen after vendor callback.
 *
 * 활동 스택은 메모리에만 살아서 vendor redirect 다음 callback 으로 돌아오면 사라진다.
 * 그러면 OAuthCallbackPage 에서 auth activity 의 fallbackPath 를 알 수 없어 홈으로
 * 떨어지는 회귀가 있었음. 이 키가 그 fallbackPath 를 sessionStorage 에 따로 박아둔다.
 */
const RETURN_KEY = "template.authReturn.v1";

export function setAuthReturnPath(path: string): void {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.setItem(RETURN_KEY, path);
  } catch {
    // Quota exceeded — caller has no recovery path.
  }
}

export function consumeAuthReturnPath(): string | null {
  const storage = safeGetStorage();
  if (!storage) return null;
  const v = storage.getItem(RETURN_KEY);
  storage.removeItem(RETURN_KEY);
  return v;
}

export function discardAuthReturnPath(): void {
  const storage = safeGetStorage();
  if (!storage) return;
  storage.removeItem(RETURN_KEY);
}

export const __AUTH_RETURN_KEY__ = RETURN_KEY;

/**
 * 소셜 가입 흐름의 잔여 sessionStorage 상태를 모두 비운다. 로그아웃 시 호출.
 *
 * 왜 필요한가:
 *   sessionStorage 는 Capacitor WebView 에서 `window.location.replace('/')`(하드 리로드)를
 *   넘어 생존한다(탭/컨텍스트가 안 죽으므로). 로그아웃이 토큰만 clear 하고 이 키들을 남기면,
 *   리로드 후 가입 흐름이 stale 한 대기표를 집어 "이미 사용된 임시 가입 토큰" 401 이 뜬다.
 *
 * 정리 대상:
 *   template-oauth-state- / -redirect- prefix → lib/socialAuth (provider OAuth CSRF state)
 *   template-signup-*  → 가입 화면 제거(2026-08-04)로 더는 쓰이지 않지만, 예전 빌드가 남긴
 *                        값이 웹뷰에 살아 있을 수 있어 한동안 함께 지운다.
 */
export function clearAuthFlowState(): void {
  discardPendingAuthIntent();
  discardAuthReturnPath();
  const storage = safeGetStorage();
  if (!storage) return;
  const EXACT_KEYS = [
    "template-signup-temp-token",
    "template-signup-provider-name",
  ];
  const PREFIXES = ["template-oauth-state-", "template-oauth-redirect-"];
  try {
    for (const key of EXACT_KEYS) storage.removeItem(key);
    // prefix 키는 개수가 가변(provider 별)이라 스냅샷 후 제거.
    const prefixed: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (k && PREFIXES.some((p) => k.startsWith(p))) prefixed.push(k);
    }
    for (const k of prefixed) storage.removeItem(k);
  } catch {
    // 일부 webview 는 storage 접근 시 throw — 무시(로그아웃은 계속).
  }
}
